import { Avatar, Flex, Heading, Text } from '@zillow/constellation';
import type { Answers, CustomAnswerInput, SentCoapplicantInvitation } from '../../../../../graphql/queries';
import { getAnswerDisplayValue, getAnswerForField } from '../../../../../utils/overview/overview-display';
import { FIELD_IDS } from '../../../../../constants/wizard';
import { pluralize } from '../../../../../utils/formatters';

interface Props {
  answers: Answers;
  isPrimaryApplicant: boolean;
}

export function OccupantsOverview({ answers, isPrimaryApplicant }: Props) {
  const numberOfOccupants = getAnswerForField({ fieldId: FIELD_IDS.OCCUPANTS, answers }) as number | null;
  const mobilityImpairedValue = getAnswerForField({ fieldId: FIELD_IDS.MOBILITY_IMPAIRED, answers }) as
    | CustomAnswerInput
    | undefined;

  return (
    <>
      {numberOfOccupants && <Text>{pluralize('occupant', numberOfOccupants)}</Text>}
      {numberOfOccupants && isPrimaryApplicant && <CoApplicantOverview answers={answers} />}
      {mobilityImpairedValue && (
        <>
          <Heading level={6} marginBottom="xs" marginTop="sm">
            Disability accommodation
          </Heading>
          <Text>{getAnswerDisplayValue(mobilityImpairedValue.value)}</Text>
        </>
      )}
    </>
  );
}

function CoApplicantOverview({ answers }: { answers: Answers }) {
  const coapplicants =
    (getAnswerForField({ fieldId: FIELD_IDS.CO_APPLICANTS, answers }) as Array<SentCoapplicantInvitation>) || [];
  const firstName = getAnswerForField({ fieldId: FIELD_IDS.FIRST_NAME, answers });
  const lastName = getAnswerForField({ fieldId: FIELD_IDS.LAST_NAME, answers });
  return (
    <ul style={{ padding: 0 }}>
      <Flex display="flex" alignItems="center" marginTop="sm" as="li">
        <Avatar marginRight="sm" fullName={`${firstName}${lastName}`} background="blue500" size="sm" />
        <Flex display="flex" flexDirection="column">
          <Text fontType="bodySmall">
            {firstName} {lastName} (you)
          </Text>
        </Flex>
      </Flex>
      {coapplicants &&
        coapplicants.length > 0 &&
        coapplicants.map((applicant) => (
          <Flex key={applicant.id} display="flex" alignItems="center" marginTop="sm" as="li">
            <Avatar
              marginRight="sm"
              fullName={`${applicant.firstName}${applicant.lastName}`}
              background="blue500"
              size="sm"
            />
            <Flex display="flex" flexDirection="column">
              <Text fontType="bodySmall">
                {applicant.firstName} {applicant.lastName}
              </Text>
              <Text fontType="finePrint" fontColor="textMedium">
                {applicant.email}
              </Text>
            </Flex>
          </Flex>
        ))}
    </ul>
  );
}
